import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  EmailOutlined as EmailIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  getTwoFactorStatus,
  setupTwoFactor,
} from "@accredo/fe-shared/services/twoFactorService.js";
import TwoFactorVerification from "@accredo/fe-shared/components/auth/TwoFactorVerification.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";

/**
 * TwoFactorSettings Component
 * Provides interface for users to manage their 2FA settings
 */
export default function TwoFactorSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [setupDialog, setSetupDialog] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadTwoFactorStatus();
  }, []);

  const loadTwoFactorStatus = async () => {
    try {
      await getTwoFactorStatus({
        setIsLoading,
        onSuccess: (data) => {
          console.log("setting 2fa staus", data);
          setTwoFactorStatus(data);
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message ||
            "Fehler beim Laden der 2FA-Einstellungen.";
          enqueueSnackbar(errorMessage, { variant: "error" });
          console.error("2FA Status Error:", error);
        },
      });
    } catch (error) {
      console.error("Error loading 2FA status:", error);
      enqueueSnackbar(
        "Unerwarteter Fehler beim Laden der 2FA-Einstellungen. Bitte laden Sie die Seite neu.",
        { variant: "error" }
      );
    }
  };

  const handleSetupTwoFactor = async () => {
    try {
      await setupTwoFactor("email", {
        setIsLoading,
        onSuccess: (data) => {
          setSetupData(data);
          setSetupDialog(true);
          enqueueSnackbar(
            "2FA-Setup gestartet. Verifikationscode wurde gesendet.",
            { variant: "success" }
          );
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message || "Fehler beim Einrichten der 2FA.";
          enqueueSnackbar(errorMessage, { variant: "error" });
        },
      });
    } catch (error) {
      console.error("Error setting up 2FA:", error);
    }
  };

  const handleVerifySetup = async () => {
    // setup verification of the method completed successfully
    setSetupDialog(false);
    setSetupData(null);
    await loadTwoFactorStatus(); // refresh the status
    enqueueSnackbar("2FA wurde erfolgreich eingerichtet.", {
      variant: "success",
    });
  };

  const handleCancelSetup = () => {
    setSetupDialog(false);
    setSetupData(null);
  };

  if (isLoading && !twoFactorStatus) {
    return <Spinner />;
  }

  const hasProviders = twoFactorStatus?.providers?.length > 0;
  const emailProvider = twoFactorStatus?.providers?.find(
    (p) => p.type === "email"
  );
  console.log("providers", twoFactorStatus?.providers);
  console.log("emailProvider", emailProvider);

  return (
    <>
      <Typography
        variant="h5"
        sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}
      >
        Zwei-Faktor-Authentifizierung
      </Typography>

      {/* Status Overview */}
      <Box sx={{ mb: 2, mt: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Die Zwei-Faktor-Authentifizierung bietet zusätzliche Sicherheit für
          Ihr Konto, indem sie einen zweiten Verifizierungsschritt erfordert.
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Status
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: hasProviders ? "success.main" : "warning.main",
            }}
          >
            {hasProviders ? (
              <CheckIcon fontSize="small" />
            ) : (
              <WarningIcon fontSize="small" />
            )}
            {hasProviders ? "aktiviert" : "nicht aktiviert"}
          </Typography>
        </Box>
      </Box>

      {/* Providers List */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Verfügbare Methoden
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <EmailIcon color="inherit" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  E-Mail-Verifikation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {emailProvider
                    ? `Eingerichtet am ${getDatum(emailProvider.created_at)}`
                    : "Verifikationscodes werden an Ihre E-Mail-Adresse gesendet"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {emailProvider ? (
                  <Chip
                    label={
                      emailProvider.is_verified
                        ? "Verifiziert"
                        : "Nicht verifiziert"
                    }
                    color={emailProvider.is_verified ? "success" : "warning"}
                    size="small"
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSetupTwoFactor}
                    disabled={!twoFactorStatus?.is_enabled}
                  >
                    Einrichten
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Security Information */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Sicherheitshinweise
        </Typography>

        <Box sx={{ pl: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            • Codes sind 15 Minuten gültig und können nur einmal verwendet
            werden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            • Stellen Sie sicher, dass Sie Zugang zu Ihrer registrierten
            E-Mail-Adresse haben
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Kontaktieren Sie den Support, falls Sie keinen Zugang zu Ihren
            2FA-Methoden haben
          </Typography>
        </Box>
      </Box>

      {/* Setup Dialog */}
      <Dialog
        open={setupDialog}
        onClose={handleCancelSetup}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>2FA einrichten</DialogTitle>
        <DialogContent>
          {setupData && (
            <TwoFactorVerification
              email={twoFactorStatus?.user?.email || ""}
              challengeData={{
                challenge_id: setupData.provider_id,
                expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
              }}
              onVerificationSuccess={handleVerifySetup}
              onCancel={handleCancelSetup}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
